const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

class FilterDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", (e) => {
      if (!this.summary.contains(e.target)) return;

      e.preventDefault();

      if (this._allowToggle) {
        this.details.open ? this.close() : this.open();
      }
    });

    this.lastAllowToggle = null;

    const toggleOpen = () => {
      if (!this._allowToggle) {
        this._forceOpen();
        this.summary.tabIndex = -1;
      } else if (this.lastAllowToggle === false && this._allowToggle) {
        this._forceClosed();
        this.summary.removeAttribute("tabindex");
      }

      this.lastAllowToggle = this._allowToggle;
    };

    toggleOpen();

    this.onResize = toggleOpen;

    window.addEventListener("resize", this.onResize);

    this.addEventListener("click", (e) => {
      if (!e.target.closest("[data-close]")) return;

      if (this._allowToggle && e.target.closest("filter-drawer") === this) {
        this.close();
      }
    });

    this.addEventListener("filter-drawer:force-closed", () => {
      if (this._allowToggle) {
        this._forceClosed();
      }
    });

    this.addEventListener("filter-drawer:close", () => {
      if (this._allowToggle) {
        this.close();
      }
    });

    this.handleChange = (e) => {
      if (this._allowToggle && this._closeOnChange) {
        // Brief delay so user can register their change before drawer closes
        setTimeout(() => {
          this.close();
        }, 250);
      }
    };

    this.addEventListener("change", this.handleChange);
    this.addEventListener("aria-listbox:change", this.handleChange);

    this.handleDocumentClick = (e) => {
      if (
        this.details.open &&
        this._allowToggle &&
        !this.contains(e.target) &&
        !e.target.hasAttribute("data-remove-filter")
      ) {
        this.close();
      }
    };

    this.handleKeyUp = (e) => {
      if (
        this.details.open &&
        this._allowToggle &&
        e.code.toUpperCase() === "ESCAPE"
      ) {
        this.close();
      }
    };

    document.addEventListener("keyup", this.handleKeyUp);
    document.addEventListener("click", this.handleDocumentClick);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("keyup", this.handleKeyUp);
    document.removeEventListener("click", this.handleDocumentClick);
  }

  _forceOpen() {
    this.removeEventListener("transitionend", this.handleTransitionEnd);
    this.details.classList.add("open");
    this.details.open = true;

    if (!this._isModal) {
      if (this.focusIsTrapped) {
        window.removeTrapFocus(this.returnFocusEl, this);
        this.focusIsTrapped = false;
      }

      window.unlockScroll(this);
    }
  }

  _forceClosed() {
    this.removeEventListener("transitionend", this.handleTransitionEnd);
    this.details.classList.remove("open");
    this.details.open = false;

    if (this.focusIsTrapped) {
      window.removeTrapFocus(this.returnFocusEl, this);
      this.focusIsTrapped = false;
    }

    window.unlockScroll(this);
  }

  open() {
    if (this.details.open) return;
    this.summary.classList.add("is-active");
    this.removeEventListener("transitionend", this.handleTransitionEnd);
    window.openFullDimmer(this);

    this.handleTransitionEnd = () => {
      this.removeEventListener("transitionend", this.handleTransitionEnd);
      this.details.style.setProperty("--filter-content-height", "auto");

      const targetListbox = this._targetListbox;
      if (targetListbox) {
        targetListbox.setAttribute("active", "");
      }
    };

    this.addEventListener("transitionend", this.handleTransitionEnd);

    this.details.open = true;

    if (this._isModal) {
      window.lockScroll(this);
      this.returnFocusEl = document.activeElement;
      window.trapFocus(this, this.querySelector("[data-close]"));
      this.focusIsTrapped = true;
    }

    window.requestAnimationFrame(() => {
      this.details.style.setProperty(
        "--filter-content-height",
        `${this.content.scrollHeight}px`,
      );
      window.requestAnimationFrame(() => {
        this.details.classList.add("open");
      });
    });
  }

  close() {
    if (!this.details.open) return;
    this.summary.classList.remove("is-active");
    window.closeFullDimmer(this);

    if (this.content) {
      this.removeEventListener("transitionend", this.handleTransitionEnd);

      this.handleTransitionEnd = () => {
        this.removeEventListener("transitionend", this.handleTransitionEnd);
        this.details.style.removeProperty("--filter-content-height");
        this.details.open = false;

        if (this.focusIsTrapped) {
          window.removeTrapFocus(this.returnFocusEl, this);
          this.focusIsTrapped = false;
        }

        window.unlockScroll(this);
      };

      this.addEventListener("transitionend", this.handleTransitionEnd);

      const targetListbox = this._targetListbox;
      if (targetListbox) {
        targetListbox.removeAttribute("active");
      }
      this.details.style.setProperty(
        "--filter-content-height",
        `${this.content.scrollHeight}px`,
      );

      window.requestAnimationFrame(() => {
        this.details.classList.remove("open");
      });
    } else {
      this.details.open = false;
      this.details.classList.remove("open");
    }
  }

  get _allowToggle() {
    return true;
  }

  get _isModal() {
    return true;
  }

  get _targetListbox() {
    return document.getElementById(this.getAttribute("target-listbox"));
  }

  get details() {
    return this.querySelector("details");
  }

  get content() {
    return this.querySelector("[data-content]");
  }

  get summary() {
    return this.querySelector("summary");
  }

  get _closeOnChange() {
    const closeOnChangeWhenQuery =
      this.getAttribute("close-on-change-when") || null;

    if (!closeOnChangeWhenQuery) return false;

    return window.matchMedia(closeOnChangeWhenQuery).matches;
  }
}

if (!customElements.get("filter-drawer")) {
  customElements.define("filter-drawer", FilterDrawer);
}
